import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api.ts';
import { Spinner, cx } from '../../components/ui.tsx';
import {
  ITEM_LABEL,
  coveredBy,
  depthOf,
  describeSelection,
  itemKey,
  visibleFolders,
  withoutRedundant,
  type PickableFolder,
  type PickableItem,
} from './folder-grants.ts';

export interface AccessSelection {
  readonly folders: ReadonlySet<string>;
  /** Keyed by `kind:id`, holding enough of each item to draw it. */
  readonly items: ReadonlyMap<string, PickableItem>;
}

/** The Drive root, which cannot be granted but does hold loose files. */
const ROOT = { id: null as string | null, name: 'Drive root', path: '/' };

/**
 * What an account may see: whole folders, and single files out of folders it
 * cannot.
 *
 * Two columns, because there are two ways to find a file and only one of them
 * was here before. Searching works when you know roughly what the thing is
 * called; the names in this office are scans, goods declarations and
 * customer-initialled ledgers, so more often you know *where* it is and not
 * what it is called. The left column is the folder tree to look through, the
 * right is what is filed in whichever folder is open — the same shape as the
 * Drive itself, because that is where these files were last seen.
 *
 * Searching still works and searches both: the left column narrows to matching
 * folders, the right becomes matching files from anywhere in the Drive.
 *
 * What is chosen sits along the bottom as chips rather than only as ticks. A
 * selection you can only see by going back to where you ticked it is one
 * nobody trusts: open a folder, and the thing you picked two folders ago is
 * off the screen. The chips are the answer to "what have I actually given
 * them", visible the whole time and removable from where they are read.
 */
export function AccessPicker({
  selection,
  onChange,
}: {
  selection: AccessSelection;
  onChange(next: AccessSelection): void;
}) {
  const [term, setTerm] = useState('');
  const [openFolder, setOpenFolder] = useState<{ id: string | null; name: string }>(ROOT);
  const search = useDebounced(term, 200);
  const searching = search.trim().length >= 2;

  const folderQuery = useQuery({
    queryKey: ['users', 'folders'],
    queryFn: () => api.get<PickableFolder[]>('/api/users/folders'),
    staleTime: 30_000,
  });

  /**
   * What the right column shows: a folder's contents, or what a search found.
   *
   * One query with two shapes rather than two queries, so the column can
   * never briefly show both — the request that answers is the one whose key
   * is current.
   */
  const itemQuery = useQuery({
    queryKey: ['users', 'items', searching ? `q:${search}` : `in:${openFolder.id ?? 'root'}`],
    queryFn: () =>
      api.get<PickableItem[]>(
        searching
          ? `/api/users/items?q=${encodeURIComponent(search)}`
          : // No parameter at all for the root, rather than an empty one —
            // which is what the server was being asked to make sense of.
            `/api/users/items${openFolder.id ? `?folderId=${openFolder.id}` : ''}`,
      ),
    staleTime: 30_000,
  });

  const folders = useMemo(() => folderQuery.data ?? [], [folderQuery.data]);
  const covered = useMemo(
    () => coveredBy(selection.folders, folders),
    [selection.folders, folders],
  );
  const shownFolders = useMemo(() => visibleFolders(folders, term), [folders, term]);
  const items = itemQuery.data ?? [];

  const toggleFolder = (id: string) => {
    const next = new Set(selection.folders);
    if (next.has(id)) next.delete(id);
    else next.add(id);

    // Ticking a parent absorbs the children already ticked under it, so what
    // is held is always the shortest way of saying it.
    onChange({ ...selection, folders: new Set(withoutRedundant(next, folders)) });
  };

  const toggleItem = (item: PickableItem) => {
    const next = new Map(selection.items);
    const key = itemKey(item);
    if (next.has(key)) next.delete(key);
    else next.set(key, item);
    onChange({ ...selection, items: next });
  };

  if (folderQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-10 text-body-sm text-muted">
        <Spinner /> Loading the Drive…
      </div>
    );
  }

  const chosenFolders = folders.filter((folder) => selection.folders.has(folder.id));
  const chosenItems = [...selection.items.values()];
  const anyChosen = chosenFolders.length > 0 || chosenItems.length > 0;

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="flex items-center gap-2 border-b border-border bg-[var(--surface-sunken)] px-2 py-2">
        <SearchGlyph />
        <input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Search folders and files, or open a folder below"
          aria-label="Search folders and files"
          className="h-8 w-full rounded-md border border-border-input bg-[var(--surface)] px-2 text-body-sm text-ink"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        {/* ---- left: the folders -------------------------------------- */}
        <div className="border-b border-border sm:border-b-0 sm:border-r">
          <ColumnHeading>Folders</ColumnHeading>

          <ul className="h-[min(16rem,32vh)] overflow-y-auto py-1">
            {!searching ? (
              <FolderRow
                name={ROOT.name}
                open={openFolder.id === null}
                onOpen={() => setOpenFolder(ROOT)}
                /* The root is not a folder and no grant can name it, so it is
                   a place to look rather than something to tick. Its loose
                   files can still be given one by one, on the right. */
                hint="loose files"
              />
            ) : null}

            {shownFolders.length === 0 ? (
              <li className="px-3 py-3 text-body-sm text-muted">No folder matches that.</li>
            ) : (
              shownFolders.map((folder) => {
                const inherited = covered.has(folder.id);

                return (
                  <FolderRow
                    key={folder.id}
                    name={folder.name}
                    path={searching && depthOf(folder.path) > 0 ? folder.path : undefined}
                    indent={searching ? 0 : depthOf(folder.path)}
                    checked={selection.folders.has(folder.id) || inherited}
                    disabled={inherited}
                    dim={!folder.matched}
                    badge={inherited ? 'included' : undefined}
                    open={openFolder.id === folder.id}
                    onToggle={() => toggleFolder(folder.id)}
                    onOpen={() => {
                      setOpenFolder({ id: folder.id, name: folder.name });
                      // Opening a folder is asking what is in it, and the
                      // search box is answering a different question.
                      setTerm('');
                    }}
                  />
                );
              })
            )}
          </ul>
        </div>

        {/* ---- right: what is in the open folder ----------------------- */}
        <div>
          <ColumnHeading>
            {searching ? (
              <>Files matching “{search.trim()}”</>
            ) : (
              <span className="truncate">In {openFolder.name}</span>
            )}
          </ColumnHeading>

          <ul className="h-[min(16rem,32vh)] overflow-y-auto py-1">
            {itemQuery.isPending ? (
              <li className="flex items-center gap-2 px-3 py-3 text-body-sm text-muted">
                <Spinner /> Loading…
              </li>
            ) : itemQuery.isError ? (
              /*
               * A failed request is not an empty folder, and drawing it as one
               * is how a 500 went unnoticed: the Drive root held files, the
               * request for them errored, and the column said nothing is filed
               * in here — which is a sentence, and wrong.
               */
              <li className="px-3 py-3 text-body-sm text-[var(--danger)]">
                Could not load what is in here. The folders on the left still work.
              </li>
            ) : items.length === 0 ? (
              <li className="px-3 py-3 text-body-sm text-muted">
                {searching
                  ? 'No file matches that.'
                  : 'Nothing is filed directly in here. Open a folder on the left to look inside it.'}
              </li>
            ) : (
              items.map((item) => (
                <ItemRow
                  key={itemKey(item)}
                  item={item}
                  checked={selection.items.has(itemKey(item))}
                  showPath={searching}
                  onToggle={() => toggleItem(item)}
                />
              ))
            )}
          </ul>
        </div>
      </div>

      {anyChosen ? (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border px-2.5 py-2">
          {chosenFolders.map((folder) => (
            <Chip
              key={folder.id}
              label={folder.name}
              tone="folder"
              onRemove={() => toggleFolder(folder.id)}
            />
          ))}
          {chosenItems.map((item) => (
            <Chip
              key={itemKey(item)}
              label={item.name}
              tone="item"
              onRemove={() => toggleItem(item)}
            />
          ))}
          <button
            type="button"
            onClick={() => onChange({ folders: new Set(), items: new Map() })}
            className="ml-auto shrink-0 rounded-md px-2 py-1 text-micro text-[var(--blue-700)] transition-fast hover:bg-[var(--blue-50)]"
          >
            Clear all
          </button>
        </div>
      ) : null}

      <p className="border-t border-border bg-[var(--surface-sunken)] px-3 py-2 text-micro text-muted">
        {describeSelection(chosenFolders.length, chosenItems.length, covered.size)}
      </p>
    </div>
  );
}

function ColumnHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex h-8 items-center border-b border-border px-3 text-micro font-semibold uppercase tracking-wide text-muted">
      {children}
    </p>
  );
}

/**
 * A folder: a box to grant it, and the rest of the row to look inside it.
 *
 * Two targets in one line, which is worth being deliberate about — the box is
 * the only part that changes what the account can see, and clicking the name
 * cannot be mistaken for it because the row it opens is plainly the other
 * column changing.
 */
function FolderRow({
  name,
  path,
  indent = 0,
  checked = false,
  disabled = false,
  dim = false,
  badge,
  hint,
  open,
  onToggle,
  onOpen,
}: {
  name: string;
  path?: string | undefined;
  indent?: number;
  checked?: boolean;
  disabled?: boolean;
  dim?: boolean;
  badge?: string | undefined;
  hint?: string | undefined;
  open: boolean;
  onToggle?: (() => void) | undefined;
  onOpen(): void;
}) {
  return (
    <li>
      <div
        className={cx(
          'flex items-center gap-2 pr-2 transition-fast',
          open ? 'bg-[var(--blue-50)]' : 'hover:bg-sunken',
        )}
        style={{ paddingLeft: `${10 + indent * 14}px` }}
      >
        {onToggle ? (
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onToggle}
            aria-label={`Give access to ${name}`}
            className="h-4 w-4 shrink-0 accent-[var(--blue-600)]"
          />
        ) : (
          <span className="h-4 w-4 shrink-0" aria-hidden />
        )}

        <button
          type="button"
          onClick={onOpen}
          className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left"
        >
          <FolderGlyph />
          <span className="min-w-0 flex-1">
            <span
              title={name}
              className={cx(
                'block truncate text-body-sm',
                dim ? 'text-muted' : open ? 'text-[var(--blue-800)]' : 'text-ink',
              )}
            >
              {name}
            </span>
            {path ? <span className="block truncate text-micro text-muted">{path}</span> : null}
          </span>
          {(badge ?? hint) ? (
            <span className="shrink-0 text-micro text-muted">{badge ?? hint}</span>
          ) : null}
        </button>
      </div>
    </li>
  );
}

function ItemRow({
  item,
  checked,
  showPath,
  onToggle,
}: {
  item: PickableItem;
  checked: boolean;
  showPath: boolean;
  onToggle(): void;
}) {
  return (
    <li>
      <label className="flex cursor-pointer items-center gap-2 py-1.5 pl-3 pr-2 transition-fast hover:bg-sunken">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="h-4 w-4 shrink-0 accent-[var(--blue-600)]"
        />
        <span className="shrink-0 text-muted">
          <FileGlyph />
        </span>
        <span className="min-w-0 flex-1">
          {/* The office files scans as bookedPacketSlip_7825751 (1).pdf, which
              no column is wide enough for. Truncated with the whole name on
              hover, rather than widened to fit the worst case. */}
          <span title={item.name} className="block truncate text-body-sm text-ink">
            {item.name}
          </span>
          {showPath ? (
            <span className="block truncate text-micro text-muted">{item.path}</span>
          ) : null}
        </span>
        <span className="shrink-0 text-micro text-muted">{ITEM_LABEL[item.kind]}</span>
      </label>
    </li>
  );
}

function Chip({
  label,
  tone,
  onRemove,
}: {
  label: string;
  tone: 'folder' | 'item';
  onRemove(): void;
}) {
  return (
    <span
      className={cx(
        'inline-flex max-w-[14rem] items-center gap-1 rounded-full py-0.5 pl-2 pr-1 text-micro',
        tone === 'folder'
          ? 'bg-[var(--blue-50)] text-[var(--blue-800)]'
          : 'bg-[var(--surface-sunken)] text-body',
      )}
    >
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-fast hover:bg-[var(--blue-100)]"
      >
        <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden>
          <path
            d="M1 1l8 8M9 1l-8 8"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </span>
  );
}

/**
 * The search term, once the typing has stopped.
 *
 * Every keystroke is a query to the server otherwise, and the answers arrive
 * out of order — the list flickering between what "inv" matched and what
 * "invoi" did.
 */
function useDebounced(value: string, delay: number): string {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return settled;
}

function SearchGlyph() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="ml-1 shrink-0 text-muted"
      aria-hidden
    >
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5 14 14" strokeLinecap="round" />
    </svg>
  );
}

function FolderGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M1.5 4a1 1 0 0 1 1-1h3l1.2 1.4H13.5a1 1 0 0 1 1 1v6.1a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1z" />
    </svg>
  );
}

function FileGlyph() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      aria-hidden
    >
      <path d="M4 1.75h5L12.25 5v9.25H4z" strokeLinejoin="round" />
      <path d="M9 1.75V5h3.25" strokeLinejoin="round" />
    </svg>
  );
}
